import { OrgAddressResponse } from '@business/entities/organization';
import { FieldType, SettingFieldResponse } from '@business/entities/settingFields';
import { CommonService } from '@business/services';
import { FormDateInput, FormInput, FormInputPhone, FormSelect, FormSelectItem } from '@core/components/form';
import { FormHTML } from '@core/components/form/FormHTML';
import { FormRadio, FormRadioGroup } from '@core/components/form/FormRadio';
import { FormControl, FormGroupData } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { R } from '@core/utils/r';
import React, { Fragment } from 'react';
import { Label } from 'reactstrap';
import { HTMLService } from '../html_service';
import { OrgService } from '../org_service';
import { AddressCustomField, CustomAddressForm } from './AddressCustomField';
import { FormUpload } from '@core/components/form/FormUpload';
import { CustomFieldResponseType } from '@business/entities/integration';
import { UserResponse } from '@business/entities/account';

export function getCustomFieldJSX(
  field: SettingFieldResponse,
  control: FormControl<any> | FormGroupData<any>,
  allMultiple?: boolean,
  users?: UserResponse[],
) {
  if (field.type === FieldType.user) {
    return (
      <FormSelect
        isClearable
        key={field.id}
        control={control as FormControl<any>}
        items={CommonService.selectItems(users || [], u => CommonService.getFullName(u), 'id', true)}
      />
    );
  } else if (field.type === FieldType.text) {
    return <FormInput key={field.id} control={control as FormControl<any>} />;
  } else if (field.type === FieldType.phone) {
    return <FormInputPhone key={field.id} countryCode={field.value} control={control as FormControl<any>} />;
  } else if (field.type === FieldType.address) {
    return <AddressCustomField key={field.id} form={control as CustomAddressForm} />;
  } else if (field.type === FieldType.largetext) {
    return <FormInput key={field.id} type="textarea" control={control as FormControl<any>} />;
  } else if (field.type === FieldType.numerical) {
    return <FormInput key={field.id} type="number" control={control as FormControl<any>} />;
  } else if (field.type === FieldType.datepicker) {
    return <FormDateInput key={field.id} clearable control={control as FormControl<any>} />;
  } else if (field.type === FieldType.file) {
    return <FormUpload key={field.id} control={control as FormControl<File | null | string>} />;
  } else if (field.type === FieldType.html) {
    return <FormHTML key={field.id} control={control as FormControl<any>} />;
  } else if (field.type === FieldType.autocomplete) {
    return <FormInput key={field.id} control={control as FormControl<any>} />;
  } else if (field.type === FieldType.dropdown) {
    const items = CommonService.selectItems(field.values, 'value', 'objectId');
    return (
      <FormSelect
        type={allMultiple ? 'multiple' : 'single'}
        key={field.id}
        control={control as FormControl<any>}
        items={items}
        disabled={CommonService.isWiseContentDisabled(field, control.value)}
      />
    );
  } else if (field.type === FieldType.multiOptions) {
    const items = CommonService.selectItems(field.values, 'value', 'objectId');
    return <FormSelect key={field.id} type="multiple" control={control as FormControl<any>} items={items} />;
  } else if (field.type === FieldType.singleOption) {
    return (
      <Fragment key={field.id}>
        <Label>{field.label}</Label>

        <FormRadioGroup control={control as FormControl<any>}>
          {field.values.map((value, index) => (
            <FormRadio key={index} label={value.value} value={value.value} />
          ))}
        </FormRadioGroup>
      </Fragment>
    );
  }
  return <div />;
}

export function getCustomFieldSelectJSXMapping(field: SettingFieldResponse, control: FormControl<any> | FormGroupData<any>, items: FormSelectItem[]) {
  return <FormSelect items={items} key={field.id} control={control as FormControl<any>} />;
}

export function getCustomFieldJSXMapping(field: SettingFieldResponse, control: FormControl<any> | FormGroupData<any>) {
  if (field.type !== FieldType.address) {
    return <FormInput flex={[1, 2]} key={field.id} control={control as FormControl<any>} />;
  } else {
    const form = control as CustomAddressForm;
    return (
      <Fragment>
        <FormInput flex={[1, 2]} control={form.controls.address} />
        <FormInput flex={[1, 2]} control={form.controls.addressLineTwo} />
        <FormInput flex={[1, 2]} control={form.controls.country} />
        <FormInput flex={[1, 2]} control={form.controls.city} />
        <FormInput flex={[1, 2]} control={form.controls.country} />
        <FormInput flex={[1, 2]} control={form.controls.state} />
        <FormInput flex={[1, 2]} control={form.controls.postal} />
      </Fragment>
    );
  }
}

export function getFieldValueString(field: SettingFieldResponse, value: CustomFieldResponseType) {
  try {
    if (field.type === FieldType.text) {
      return value as string;
    } else if (field.type === FieldType.user) {
      return value as string;
    } else if (field.type === FieldType.phone) {
      return value as string;
    } else if (field.type === FieldType.address) {
      const address = value as OrgAddressResponse;
      return OrgService.getAddressString(address);
    } else if (field.type === FieldType.largetext) {
      return value as string;
    } else if (field.type === FieldType.numerical) {
      return value as string;
    } else if (field.type === FieldType.file) {
      if (value && R.get(value, 'url') && R.get(value, 'fileName')) {
        return (
          <a href={R.get(value, 'url')} target="_blank" rel="noopener noreferrer">
            {R.get(value, 'fileName')}
          </a>
        );
      }
      return <div></div>;
    } else if (field.type === FieldType.datepicker) {
      return Formatter.formatTime(value as number);
    } else if (field.type === FieldType.html) {
      return HTMLService.toReact((value as string) || '<p></p>');
    } else if (field.type === FieldType.autocomplete) {
      return value as string;
    } else if (field.type === FieldType.dropdown) {
      return field.values.find(v => v.objectId == value)?.value || '';
    } else if (field.type === FieldType.multiOptions) {
      const values: string[] = [];
      const parseValues = JSON.parse((value as string) || '[]') as string[];
      const selectedValues: string[] = parseValues.map(s => R.toString(s));
      field.values.forEach(inField => {
        if (selectedValues.includes(R.toString(inField.objectId || ''))) {
          values.push(inField.value);
        }
      });
      return values.join(', ');
    } else if (field.type === FieldType.singleOption) {
      return value as string;
    }
  } catch (err) {
    return <div />;
  }
  return <div />;
}
